import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Send, Upload, Mic, ChevronRight } from 'lucide-react';
import { API_URL } from '../constants';

const ContributePage = () => {
  const { type = 'archive' } = useParams();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [form, setForm] = useState({ firstName:'', lastName:'', contact:'', village:'', ward:'', category:'', subcategory:'', contributionName:'', details:'', image:null, voiceRecording:null });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/categories/`).then(r => r.json()).then(d => setCategories(Array.isArray(d) ? d : (d.results || []))).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.category) {
      fetch(`${API_URL}/subcategories/?category=${form.category}`).then(r => r.json()).then(d => setSubcategories(Array.isArray(d) ? d : (d.results || []))).catch(() => {});
    }
  }, [form.category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.contact || !form.details) { setError('Please fill in all required fields.'); return; }
    setLoading(true); setError('');
    const fd = new FormData();
    fd.append('contribution_type', type);
    fd.append('first_name', form.firstName);
    fd.append('last_name', form.lastName);
    fd.append('contact', form.contact);
    if (form.village) fd.append('village', form.village);
    if (form.ward) fd.append('ward', form.ward);
    if (form.category) fd.append('category', form.category);
    if (form.subcategory) fd.append('subcategory', form.subcategory);
    if (form.contributionName) fd.append('contribution_name', form.contributionName);
    fd.append('details', form.details);
    if (form.image) fd.append('image', form.image);
    if (form.voiceRecording) fd.append('voice_recording', form.voiceRecording);
    try {
      const res = await fetch(`${API_URL}/contributions/`, { method:'POST', body:fd });
      if (!res.ok) throw new Error('Submission failed.');
      setSubmitted(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const typeLabels = { archive:'Archive Contribution', news:'News Submission', person:'Person Profile', cultural:'Cultural Content', contact:'Contact Us' };
  const typeIcons = { archive:'📚', news:'📰', person:'👤', cultural:'🎭', contact:'📬' };

  if (submitted) return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center">
      <div className="text-6xl mb-4">✅</div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
      <p className="text-gray-600 mb-6">Your contribution has been submitted and is pending review by our team.</p>
      <div className="flex gap-3 justify-center">
        <Link to="/" className="bg-emerald-700 text-white px-5 py-2.5 rounded-xl hover:bg-emerald-800 transition">Go Home</Link>
        <button onClick={() => { setSubmitted(false); setForm({ firstName:'',lastName:'',contact:'',village:'',ward:'',category:'',subcategory:'',contributionName:'',details:'',image:null,voiceRecording:null }); }} className="border border-gray-300 text-gray-700 px-5 py-2.5 rounded-xl hover:bg-gray-50 transition">Submit Another</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-emerald-600">Home</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900">Submit Contribution</span>
      </nav>

      {/* Type selector */}
      <div className="flex gap-2 flex-wrap mb-6">
        {Object.entries(typeLabels).map(([key, label]) => (
          <Link key={key} to={`/contribute/${key}`}
            className={`px-3 py-1.5 rounded-xl text-sm font-medium transition ${type === key ? 'bg-emerald-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {typeIcons[key]} {label}
          </Link>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-emerald-700 text-white px-6 py-5">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Send className="w-5 h-5" /> {typeIcons[type]} {typeLabels[type] || 'Contribute'}
          </h1>
          <p className="text-emerald-200 text-sm mt-1">Help preserve Gbongan's heritage</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input type="text" required value={form.firstName} onChange={e => setForm(p=>({...p,firstName:e.target.value}))} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input type="text" value={form.lastName} onChange={e => setForm(p=>({...p,lastName:e.target.value}))} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact (Phone/Email) *</label>
            <input type="text" required value={form.contact} onChange={e => setForm(p=>({...p,contact:e.target.value}))} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Village/Town</label>
              <input type="text" value={form.village} onChange={e => setForm(p=>({...p,village:e.target.value}))} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ward</label>
              <input type="text" value={form.ward} onChange={e => setForm(p=>({...p,ward:e.target.value}))} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
            </div>
          </div>

          {['archive','person'].includes(type) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value,subcategory:''}))} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500">
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {subcategories.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                  <select value={form.subcategory} onChange={e => setForm(p=>({...p,subcategory:e.target.value}))} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500">
                    <option value="">Select subcategory</option>
                    {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name/Title of Contribution</label>
                <input type="text" value={form.contributionName} onChange={e => setForm(p=>({...p,contributionName:e.target.value}))} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Details / Description *</label>
            <textarea required rows="5" value={form.details} onChange={e => setForm(p=>({...p,details:e.target.value}))} placeholder={type === 'contact' ? 'Write your message...' : 'Provide information about your contribution...'} className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload Photo</label>
              <label className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 rounded-xl cursor-pointer hover:bg-gray-200 transition w-full">
                <Upload className="w-4 h-4 text-gray-600" /> {form.image ? form.image.name.substring(0,20)+'…' : 'Choose Image'}
                <input type="file" accept="image/*" className="hidden" onChange={e => setForm(p=>({...p,image:e.target.files[0]}))} />
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Voice Recording</label>
              <label className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 rounded-xl cursor-pointer hover:bg-gray-200 transition w-full">
                <Mic className="w-4 h-4 text-gray-600" /> {form.voiceRecording ? form.voiceRecording.name.substring(0,20)+'…' : 'Upload Audio'}
                <input type="file" accept="audio/*" className="hidden" onChange={e => setForm(p=>({...p,voiceRecording:e.target.files[0]}))} />
              </label>
            </div>
          </div>

          <button type="submit" disabled={loading} className="w-full bg-emerald-700 text-white py-3 rounded-xl font-semibold hover:bg-emerald-800 transition disabled:opacity-60 flex items-center justify-center gap-2">
            <Send className="w-4 h-4" />{loading ? 'Submitting…' : 'Submit Contribution'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContributePage;
